export type DeepEqualOptions = {
  ignoreMissingKeys?: boolean;
  ignoreOrder?: boolean;
  ignoreKeys?: string[];
  ignoreUndefinedMissingKeys?: boolean;
  stringEqualFn?: (a: string | null | undefined, b: string | null | undefined) => boolean;
};

const defaultOptions: DeepEqualOptions = {
  ignoreMissingKeys: false,
  ignoreOrder: false,
  ignoreKeys: [],
  ignoreUndefinedMissingKeys: false,
  stringEqualFn: (a, b) => a === b,
};

export function deepEqual<T>(a: T, b: T, options?: DeepEqualOptions): boolean {
  const mergedOptions = { ...defaultOptions, ...options };

  if (a === b) return true;

  if ((a === null || a === undefined) && (b === null || b === undefined)) return true;

  const isAStringLike = typeof a === 'string' || a === null || a === undefined;
  const isBStringLike = typeof b === 'string' || b === null || b === undefined;

  if (isAStringLike && isBStringLike) {
    const aStr = a as string | null | undefined;
    const bStr = b as string | null | undefined;

    return mergedOptions.stringEqualFn(aStr, bStr);
  }

  if (typeof a !== 'object' || typeof b !== 'object') {
    return a === b;
  }

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    return compareArrays(a, b, mergedOptions);
  }

  if (a === null || b === null) return false;

  const aKeys = Object.keys(a).filter((key) => !mergedOptions.ignoreKeys.includes(key));
  const bKeys = Object.keys(b).filter((key) => !mergedOptions.ignoreKeys.includes(key));

  const allKeys = new Set([...aKeys, ...bKeys]);

  for (const key of allKeys) {
    const aHasKey = a.hasOwnProperty(key);
    const bHasKey = b.hasOwnProperty(key);
    const aVal = (a as any)[key];
    const bVal = (b as any)[key];

    if (!aHasKey || !bHasKey) {
      if (mergedOptions.ignoreMissingKeys) {
        continue;
      }

      if (mergedOptions.ignoreUndefinedMissingKeys) {
        const isNullishA = aVal === undefined || aVal === null;
        const isNullishB = bVal === undefined || bVal === null;

        if (isNullishA || isNullishB) {
          continue;
        }
      }

      return false;
    }

    if (Array.isArray(aVal) && Array.isArray(bVal)) {
      if (!compareArrays(aVal, bVal, mergedOptions)) {
        return false;
      }
    } else if (!deepEqual(aVal, bVal, mergedOptions)) {
      return false;
    }
  }

  return true;
}

export type CompareArraysOptions = {
  ignoreOrder?: boolean;
  ignoreKeys?: string[];
  ignoreUndefinedMissingKeys?: boolean;
  ignoreMissingKeys?: boolean;
  stringEqualFn?: (a: string | null | undefined, b: string | null | undefined) => boolean;
};

const defaultArrayOptions: CompareArraysOptions = {
  ignoreOrder: false,
  ignoreKeys: [],
  ignoreUndefinedMissingKeys: false,
  ignoreMissingKeys: false,
  stringEqualFn: (a, b) => a === b,
};

export function compareArrays<T>(a: T[], b: T[], options = defaultArrayOptions): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  if (options.ignoreOrder) {
    const usedIndices = new Set<number>();

    return a.every((itemA) => {
      for (let i = 0; i < b.length; i++) {
        if (usedIndices.has(i)) continue;
        if (deepEqual(itemA, b[i], options)) {
          usedIndices.add(i);
          return true;
        }
      }
      return false;
    });
  }

  return a.every((item, index) => deepEqual(item, b[index], options));
}

/**
 * Removes HTML attributes that either:
 * - Start with an underscore (e.g., _customAttr)
 * - Are explicitly listed in ignoredAttributes
 */
export const removeIgnoredAttributes = (html: string, ignoredAttributes: string[] = []): string => {
  const attrPattern = [
    '_[a-zA-Z0-9-]+',
    ...ignoredAttributes.map((attr) => attr.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')), // escape regex
  ].join('|');

  const regex = new RegExp(`\\s+(${attrPattern})="[^"]*"`, 'g');
  return html.replace(regex, '');
};

/**
 * Removes any whitespace characters (spaces, tabs, etc.) directly before <br> tags.
 * Examples:
 * - "Hello <br>" → "Hello<br>"
 * - "World   <br/>" → "World<br/>"
 */
export const removeSpacesBeforeBr = (html: string): string => {
  return html.replace(/\s+(?=<br\s*\/?>)/gi, '');
};

/**
 * Removes specified HTML tags but preserves their inner content.
 * For example: <font>text</font> → text (if 'font' is in ignoredTags)
 */
export const removeIgnoredTags = (html: string, ignoredTags: string[] = []): string => {
  if (!ignoredTags.length) return html;

  const tagPattern = ignoredTags.map((tag) => tag.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')).join('|');
  const regex = new RegExp(`<\\/?(?:${tagPattern})(\\s[^>]*)?>`, 'gi');

  return html.replace(regex, '');
};

/**
 * Normalizes reference spans by replacing:
 * <span data-tt-custom-element="reference">[X]</span> → <span data-tt-custom-element="reference">[]</span>
 */
export const normalizeReferenceSpans = (html: string): string => {
  return html.replace(/<span([^>]*data-tt-custom-element="reference"[^>]*)>\[\d*]<\/span>/g, '<span$1>[]</span>');
};

/**
 * Replaces occurrences of `&nbsp;` with a single space.
 */
export const normalizeNonBreakingSpaces = (html: string): string => {
  return html.replace(/&nbsp;?/g, ' ');
};

/**
 * Runs a full normalization:
 * 1. Normalize reference spans
 * 2. Remove ignored attributes
 */
export const normalizeHtmlText = (str: string | null | undefined, ignoredAttributes: string[] = [], ignoredTags: string[] = []): string => {
  let result = str || '';
  result = normalizeReferenceSpans(result);
  result = removeIgnoredAttributes(result, ignoredAttributes);
  result = normalizeNonBreakingSpaces(result);
  result = removeIgnoredTags(result, ignoredTags);
  result = removeSpacesBeforeBr(result);

  return result;
};

export type AreGuidelineTextsEqualOptions = {
  ignoredHtmlAttributes?: string[];
  ignoredTags?: string[];
};

/**
 * Compares two HTML strings after normalization.
 */
export const compareHtmlText = (
  a: string | null | undefined,
  b: string | null | undefined,
  options?: AreGuidelineTextsEqualOptions,
): boolean => {
  const normalizedA = normalizeHtmlText(a, options?.ignoredHtmlAttributes, [...(options?.ignoredTags ?? []), 'font']);
  const normalizedB = normalizeHtmlText(b, options?.ignoredHtmlAttributes, [...(options?.ignoredTags ?? []), 'font']);

  return normalizedA === normalizedB;
};
